import { CommonHelperFunctions, ICollabOp } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../../shared/utility/main-functions";
import {
  ICollabCursor,
  ICollabJoinResponse,
  ICollabOpsResponse,
  ICollabReplayResponse,
  ICollabStateResponse,
  ICollabSyncResponse
} from "./collab-types";

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();

/**
 * REST surface of real-time collaboration. The event stream lives in
 * collab-events-stream.ts; session orchestration lives in collab-session-core.ts, driven
 * by the owning provider partition. `entityRoute` selects the entity segment of the
 * /collab/{entityRoute}/{id}/… routes and defaults to "screen" (the original screen routes).
 */
export class CollabService {
  public static async Join(
    entityId: string,
    clientId: string,
    initialJson: string,
    entityRoute: string = "screen"
  ): Promise<AxiosResponse<ICollabJoinResponse>> {
    return Axios.post<ICollabJoinResponse, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/join`, {
      clientId,
      initialJson
    });
  }

  /** Granular tree ops — the only channel that mutates the session tree (P2). */
  public static async SendOps<TOp = ICollabOp>(
    entityId: string,
    clientId: string,
    ops: TOp[],
    entityRoute: string = "screen"
  ): Promise<AxiosResponse<ICollabOpsResponse>> {
    return Axios.post<ICollabOpsResponse, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/ops`, {
      clientId,
      ops
    });
  }

  /**
   * Save metadata (publicJson / screen name) — tree content flows only through SendOps.
   * targetEntityId echoes the entity the payload was BUILT for; the server rejects the
   * sync when it differs from the session's entity (KOA-18173 cross-screen write guard).
   */
  public static async Sync(
    entityId: string,
    clientId: string,
    publicJson?: string,
    name?: string,
    targetEntityId?: string,
    entityRoute: string = "screen"
  ): Promise<AxiosResponse<ICollabSyncResponse>> {
    return Axios.post<ICollabSyncResponse, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/sync`, {
      clientId,
      publicJson,
      name,
      screenId: targetEntityId
    });
  }

  /** Gap replay after a missed seq range; 410 means the buffer no longer covers it. */
  public static async Replay(
    entityId: string,
    fromSeq: number,
    entityRoute: string = "screen"
  ): Promise<AxiosResponse<ICollabReplayResponse>> {
    return Axios.get<null, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/replay?fromSeq=${fromSeq}`);
  }

  public static async Presence(
    entityId: string,
    clientId: string,
    selectedIds: string[],
    cursor: ICollabCursor | null,
    entityRoute: string = "screen"
  ): Promise<AxiosResponse> {
    return Axios.post<null, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/presence`, {
      clientId,
      selectedIds,
      cursor
    });
  }

  public static async GetState(
    entityId: string,
    entityRoute: string = "screen"
  ): Promise<AxiosResponse<ICollabStateResponse>> {
    return Axios.get<null, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/state`);
  }

  public static async Leave(
    entityId: string,
    clientId: string,
    leaveToken: string,
    entityRoute: string = "screen"
  ): Promise<AxiosResponse | undefined> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/collab/${entityRoute}/${entityId}/leave`, {
        clientId,
        leaveToken
      });
    } catch (error: unknown) {
      // Leaving is best-effort — the server's connection-derived presence self-heals
      // within the reconnect grace even if this call never lands.
      KMainFunctions.exceptionHandler(error);
      return undefined;
    }
  }

  /**
   * Tab-close variant: navigator.sendBeacon cannot carry the Authorization header, which is
   * why the backend leave endpoint is anonymous and authenticates via the per-participant
   * leaveToken issued at join. Best-effort by design.
   */
  public static LeaveBeacon(
    entityId: string,
    clientId: string,
    leaveToken: string,
    entityRoute: string = "screen"
  ): boolean {
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return false;
    const payload: Blob = new Blob([JSON.stringify({ clientId, leaveToken })], { type: "application/json" });
    return navigator.sendBeacon(`${backendUrl}/collab/${entityRoute}/${entityId}/leave`, payload);
  }
}
